import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { CAT_DISPLAY, ORIGINAL_IMG, catIcon } from './cats';
import type { HudStore } from './store';

// The map is drawn in units where a lane is 30 high. The CSS box keeps the drawing's base shape until
// it reaches its maximum height; past that the box only gets wider, so the drawing is widened to
// the box's real shape and the lanes run edge to edge instead of leaving empty space at the sides.
// The base shape is 1000 wide, or as little as 600 on a narrow box such as a phone's, so the lanes
// and cat faces stay big enough to see there.
const LANE_H = 30, PAD_L = 60, PAD_R = 64;
const baseWidth = (boxWidth: number) => Math.round(Math.min(1000, Math.max(600, boxWidth * 1.7)));

export function RaceMap({ store }: { store: HudStore }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [box, setBox] = useState({ w: 0, shape: 0 });
  const snap = store.snapshot.value;
  const hasSnap = !!snap;
  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const measure = () => { const r = svg.getBoundingClientRect(); if (r.width > 0 && r.height > 0) setBox({ w: r.width, shape: r.width / r.height }); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(svg.parentElement!);
    return () => ro.disconnect();
  }, [hasSnap]);
  if (!snap) return null;
  const H = LANE_H * snap.cats.length + 8;
  const base = box.w ? baseWidth(box.w) : 1000;
  const W = Math.max(base, box.shape * H);
  const x = (pos: number) => PAD_L + Math.max(0, Math.min(1, pos)) * (W - PAD_L - PAD_R);
  const y = (lane: number) => 4 + lane * LANE_H + LANE_H / 2;
  return (
    <section class="panel race-map" aria-label="Race map in lane order">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ aspectRatio: `${base} / ${H}` }} role="img" aria-label={snap.cats.map((c) => `${CAT_DISPLAY[c.id].short} ${Math.round(c.signalPos * 100)}%`).join(', ')}>
        {snap.cats.map((c) => <line key={c.id} x1={x(0)} x2={x(1)} y1={y(c.lane)} y2={y(c.lane)} class="lane" />)}
        <line x1={x(0)} x2={x(0)} y1={0} y2={H} class="mark" />
        <line x1={x(1)} x2={x(1)} y1={0} y2={H} class="mark finish" />
        <text x={x(0) - 8} y={H / 2 + 4} class="end" text-anchor="end">START</text>
        <text x={x(1) + 8} y={H / 2 + 4} class="end">FINISH</text>
        {snap.cats.filter((c) => c.tripping).map((c) => (
          <image key={`psy-${c.id}`} href={ORIGINAL_IMG.psy} x={x(0)} y={y(c.lane) - 12} width={x(1) - x(0)} height={24} preserveAspectRatio="none" class="psy" data-testid={`map-psy-${c.id}`} />
        ))}
        {snap.ham && <image href={ORIGINAL_IMG.ham} x={x(snap.ham.pos) - 20} y={y(snap.ham.lane) - 5} width={40} height={10} class="item" data-testid="map-ham" />}
        {snap.catnip && <image href={ORIGINAL_IMG.catnip} x={x(snap.catnip.pos) - 12} y={y(snap.catnip.lane) - 12} width={24} height={24} class="item" data-testid="map-catnip" />}
        {snap.cats.map((c) => (
          <g key={c.id} transform={`translate(${x(c.signalPos)} ${y(c.lane)})`} class={store.followed.value === c.id ? 'marker followed' : 'marker'}>
            <circle r={14} fill={CAT_DISPLAY[c.id].colour} />
            <image href={catIcon(c.id, c)} x={-13} y={-13} width={26} height={26} data-testid={`map-icon-${c.id}`} />
          </g>
        ))}
      </svg>
    </section>
  );
}
